/**
 * QuoTrak version history, shown on Help & Support → What's New.
 *
 * Add a new release at the TOP of RELEASES every time changes are promoted to
 * production (staging → main). Write for contractors, not developers: say what
 * changed for them, not how it was built. Leave out purely internal changes
 * (tests, deploy config, marketing site) unless users would notice them.
 *
 * Versions are the release date (YYYY.MM.DD). A second release on the same day
 * gets a suffix: 2026.10.26.2. Releases before September 2026 are summarised
 * by month (YYYY.MM).
 */

export type ChangeType = "new" | "improved" | "fixed";

export type Release = {
  version: string;
  /** ISO date (YYYY-MM-DD) of the release, or the last day of a monthly summary. */
  date: string;
  /** Optional one-line headline for the release. */
  title?: string;
  changes: { type: ChangeType; text: string }[];
};

export const RELEASES: Release[] = [
  {
    version: "2026.11.04.4",
    date: "2026-11-04",
    title: "Emails that warn you in time",
    changes: [
      { type: "new", text: "If your Pro payment fails, QuoTrak now emails you right away with the date to fix it by, and again if your account moves to Free. Before, it could change plans without telling you." },
      { type: "new", text: "You'll get a reminder three days before your Pro trial ends." },
      { type: "new", text: "If a quote, invoice, reminder or receipt can't be delivered to your customer, QuoTrak emails you and notes it in your Activity page, so you can fix the address and send it again." },
      { type: "new", text: "You'll get an email whenever your password changes, and at your old address when someone asks to change your account's email, so you can act if it wasn't you." },
      { type: "fixed", text: "Resetting your password now cancels any email address change that's still waiting to be confirmed." },
      { type: "fixed", text: "The Upgrade to Pro button in the \"Automations paused\" email now opens your billing settings. It used to lead to a page that doesn't exist." },
    ],
  },
  {
    version: "2026.11.04.3",
    date: "2026-11-04",
    changes: [
      { type: "improved", text: "Every plan can now type a one-off cost on a quote line, so you can see your margin without building a catalog first. Saving costs to your catalog for reuse is still part of Pro." },
      { type: "improved", text: "Help & Support now has a simple question mark icon, in the sidebar and at the top of the page." },
    ],
  },
  {
    version: "2026.11.04.2",
    date: "2026-11-04",
    changes: [
      { type: "new", text: "Get an email as soon as a customer pays an invoice online. It's on by default, and you can turn it off in Settings under Notifications." },
      { type: "improved", text: "The Cost Report PDF is now part of Pro, as the pricing page has always listed it. The customer PDF and live margin on your quotes stay free." },
      { type: "improved", text: "The Ask AI assistant in Help & Support has been rewritten to match QuoTrak as it is today, and it knows which plan you're on." },
      { type: "improved", text: "The guides in Help & Support have been rewritten to match the app, with new sections for projects, field tools, your team, integrations and getting paid online." },
      { type: "improved", text: "The Coming soon list is gone from the Integrations page. Stripe, QuickBooks Online, Xero, Zapier and Google Sheets are the integrations QuoTrak offers." },
      { type: "improved", text: "Your logo, brand color and a custom PDF footer are now part of Pro for new accounts. If you had a QuoTrak account before today, nothing changes for you: your logo and color stay on your documents." },
      { type: "fixed", text: "Field workers now get whatever their account owner's plan includes. They could be asked to upgrade even when the owner was on Pro." },
      { type: "improved", text: "When a Pro plan or trial ends, recurring quotes and invoices now pause instead of creating new drafts, and pick back up when you upgrade." },
    ],
  },
  {
    version: "2026.11.04",
    date: "2026-11-04",
    changes: [
      { type: "improved", text: "The margin bar on a quote now runs from 0% to 100%, so a 50% margin fills half the bar and the marker sits right on your target. Changing the target only moves the marker, instead of making the same margin look different." },
      { type: "improved", text: "When you email a quote or invoice, \"Send a copy to myself\" now starts ticked. The PDF lands in your own inbox, so your customer's reply can join the same conversation with the quote or invoice right there. You can untick it for any send." },
    ],
  },
  {
    version: "2026.11.03",
    date: "2026-11-03",
    changes: [
      { type: "improved", text: "The Stair Stringer, Wall Framer and Reference charts now say clearly that they're for reference only, and to check your local code. Copied cut lists include the same reminder." },
      { type: "new", text: "Checkout now has a promo code box, so you can use a discount code when you sign up for Pro." },
      { type: "improved", text: "Wall Framer now lists the door or window size, rough opening and header for every opening, right under the drawing. Each opening has a number on the drawing that matches the list, so the numbers are there however narrow the door or long the wall." },
      { type: "fixed", text: "Your zoom setting now sticks. QuoTrak used to go back to its normal size when you reloaded or reopened the page, even though the zoom control still showed your saved size." },
    ],
  },
  {
    version: "2026.11.02",
    date: "2026-11-02",
    changes: [
      { type: "improved", text: "QuoTrak now uses American spelling, like labor, check, color and catalog, on its screens, PDFs and emails. Anything you've already saved keeps the name you gave it, and importing starter costs again won't add duplicates of the ones you have." },
    ],
  },
  {
    version: "2026.11.01.6",
    date: "2026-11-01",
    title: "Field Tools corrections",
    changes: [
      { type: "fixed", text: "Wall Framer rough openings are now the right size. The jack studs used to sit inside the opening, so a 36\" door got only 35\" of clear space and the header came out 3\" short. If you've cut from a saved wall plan with a door or window, check it again." },
      { type: "fixed", text: "Wall Framer door openings are no longer 1½\" too tall, and window sill heights now measure from the floor as labelled." },
      { type: "improved", text: "Wall Framer now lists a rough sill for each window, and warns you if an opening runs off the wall, overlaps another or doesn't fit under the top plates." },
      { type: "fixed", text: "Corrected Field Tools reference charts: concrete bag coverage (it was off by 10 times), gravel coverage per ton, drywall screws and tape, and ice and water shield rolls." },
      { type: "fixed", text: "The drywall calculator now estimates 1 lb of screws per 200 sq ft instead of 500, and the unit converter gives exact results, so 2,000 lb is 1 ton instead of 0.999999." },
    ],
  },
  {
    version: "2026.11.01.5",
    date: "2026-11-01",
    changes: [
      { type: "fixed", text: "The dollar scale on a project's Quoted vs actual chart no longer repeats itself. Amounts now read like $650 or $2.5k instead of rounding to the same label twice." },
      { type: "fixed", text: "The QuickBooks and Xero descriptions on the Integrations page now match how they work: you push invoices and quotes yourself, one at a time or in a batch. Nothing is sent automatically." },
    ],
  },
  {
    version: "2026.11.01.4",
    date: "2026-11-01",
    changes: [
      { type: "new", text: "Give a quote its own target margin. In the quote's Internal Workshop, click Change next to the target to set one for that job, or Use default to go back to the target in Settings. The quote's line items and its colour in the quotes list follow it, and duplicated or recurring copies keep it." },
    ],
  },
  {
    version: "2026.11.01.3",
    date: "2026-11-01",
    title: "Target margin",
    changes: [
      { type: "new", text: "Set a target margin in Settings → Quote Template (30% to start). The Internal Workshop shows a bar with your target marked, and when a quote falls short it tells you how much to raise the price to hit it." },
      { type: "improved", text: "Margins are coloured against your target everywhere: green at or above it, amber below it, and red under half of it or when a job loses money. That covers line item profit, the quotes list, and quoted and actual margin on projects." },
      { type: "fixed", text: "QuoTrak could clear your saved business details (name, logo, contact details and document settings) when it filled in your timezone automatically. Saves now only change what you edited, and Settings can't be saved before it has finished loading." },
    ],
  },
  {
    version: "2026.11.01.2",
    date: "2026-11-01",
    changes: [
      { type: "fixed", text: "Projects no longer count labour twice. Once your crew clocks time on a job, the hours they log replace the labour copied in from the quote, so actual cost and margin are right. The quote's labour stays on the Costs tab, greyed out, so you can compare. Labour you add by hand, materials and fees still count as before." },
      { type: "fixed", text: "Recording a payment on a milestone invoice now marks that milestone Paid in the Payment Plan. It used to keep saying \"Invoiced\" unless the customer paid online. Deleting the payment sets it back." },
    ],
  },
  {
    version: "2026.11.01",
    date: "2026-11-01",
    changes: [
      { type: "improved", text: "In the Payment Plan card, \"Progress Payments\" is now called \"Milestones\", to match the milestone invoices it creates. The tabs and the \"3 of 3\" count also stay on one line instead of wrapping." },
    ],
  },
  {
    version: "2026.10.31.5",
    date: "2026-10-31",
    title: "Clearer emails",
    changes: [
      { type: "fixed", text: "Payment receipts now say your business received the customer's payment. They used to read as if the payment came from you." },
      { type: "fixed", text: "Deposit receipts no longer say \"Paid in Full\" or that the customer's account is settled while the main invoice still has a balance." },
      { type: "fixed", text: "When a customer replies to an overdue payment reminder, the reply now goes to your reply-to address, the same as quotes, invoices and receipts." },
      { type: "improved", text: "Overdue payment reminders use your accent colour instead of red, greet the customer by name and show what the invoice is for." },
      { type: "improved", text: "Emails from QuoTrak to you (reminders, sign-in and support emails) have a fresh look that matches quotrak.com, with buttons that take you straight to the right page." },
      { type: "improved", text: "Quotes, invoices, receipts and reminders you email to customers have a cleaner header: your logo and business name side by side, with a stripe in your accent colour." },
      { type: "fixed", text: "Unsubscribe links in QuoTrak's reminder and recurring-document emails now work." },
      { type: "improved", text: "The email a new field worker gets when you add them to your team now looks like the rest of QuoTrak's emails." },
      { type: "improved", text: "All of these emails have more room for the text on phones." },
    ],
  },
  {
    version: "2026.10.31.4",
    date: "2026-10-31",
    changes: [
      { type: "fixed", text: "Removing the sample data at the end of the walkthrough no longer un-ticks the setup checklist items you'd already done." },
    ],
  },
  {
    version: "2026.10.31.3",
    date: "2026-10-31",
    title: "A simpler start for new accounts",
    changes: [
      { type: "new", text: "New accounts start with a one-minute welcome form: your business name, trade, phone, email and logo. Pick your trade and your cost catalogue fills with typical labour and material rates for it." },
      { type: "improved", text: "The setup guide is now one short checklist: business details, your first quote, sending it, getting paid, and (on Pro) card payments. Items tick themselves off as you do them, however you get there." },
      { type: "improved", text: "\"Show me\" on a checklist item walks you through it on screen. The full walkthrough is now 8 steps from quote to payment instead of 21, and you can stop it any time." },
      { type: "improved", text: "The sample customer uses your own email address, so quotes and invoices you send in the walkthrough land in your inbox and you see exactly what a customer gets." },
      { type: "fixed", text: "The guided tour could stop recognising that you'd finished a step (most often \"Save settings\"), leaving you unable to move on." },
    ],
  },
  {
    version: "2026.10.31.2",
    date: "2026-10-31",
    changes: [
      { type: "improved", text: "Pro is now priced in US dollars: $15 a month, or $144 a year ($12 a month). If you already subscribe, nothing changes: your plan keeps its current price and currency." },
    ],
  },
  {
    version: "2026.10.31",
    date: "2026-10-31",
    changes: [
      { type: "new", text: "Built a quote or invoice in the free generator on quotrak.com? Sign up from there and it's waiting in your account: line items, costs, tax, discount, deposit and terms, with the customer created for you." },
    ],
  },
  {
    version: "2026.10.28.5",
    date: "2026-10-28",
    changes: [
      { type: "new", text: "You can assign or change a project's customer from the project page: click the customer next to the project name, pick one, or create a new customer. Change orders and new quotes or invoices made from a project use this customer." },
    ],
  },
  {
    version: "2026.10.28.4",
    date: "2026-10-28",
    changes: [
      { type: "new", text: "When attaching change orders to a quote or invoice, you can now pick New quote or New invoice. QuoTrak creates a draft for the project's customer, adds it to the project and attaches the change orders in one step." },
    ],
  },
  {
    version: "2026.10.28.3",
    date: "2026-10-28",
    changes: [
      { type: "improved", text: "Quote and change order line items can have a negative price too, so you can add a discount or credit as its own line. Before, only invoices allowed it." },
    ],
  },
  {
    version: "2026.10.28.2",
    date: "2026-10-28",
    changes: [
      { type: "fixed", text: "Creating a new line item from Add Line Item, including one you save to your catalog, now closes the window once the item is added. Before, it went back to the Add Line Item search." },
    ],
  },
  {
    version: "2026.10.28",
    date: "2026-10-28",
    changes: [
      { type: "fixed", text: "The Saving/Saved note on a quote or invoice line item now shows in the item's title bar, with \"Saved\" as an easy-to-spot green badge. Before, it popped up between the title and the description and pushed the page down while you were typing." },
    ],
  },
  {
    version: "2026.10.26.2",
    date: "2026-10-26",
    changes: [
      { type: "new", text: "What's New in Help & Support: see what changed in each QuoTrak update." },
    ],
  },
  {
    version: "2026.10.26",
    date: "2026-10-26",
    changes: [
      { type: "fixed", text: "Changing a parent invoice's discount or tax now updates its unsent deposit, balance and milestone invoices straight away. Before, they only updated when a line item changed." },
    ],
  },
  {
    version: "2026.10.23",
    date: "2026-10-23",
    changes: [
      { type: "new", text: "Invoice line items can have a negative price, so you can add a discount or credit as its own line." },
      { type: "improved", text: "An invoice's total can't go below $0. If a change would do that, QuoTrak tells you why and keeps the invoice as it was." },
      { type: "fixed", text: "Deposit, balance and milestone invoices show the parent invoice's discount as its own line, instead of folding it into \"Less: deposit already invoiced\"." },
    ],
  },
  {
    version: "2026.09.29",
    date: "2026-09-29",
    changes: [
      { type: "fixed", text: "Quote and invoice PDFs no longer cut off a line item at the bottom of a page. Items that don't fit move to the next page whole." },
      { type: "fixed", text: "Group headings on PDFs stay on the same page as their first item." },
    ],
  },
  {
    version: "2026.09.27",
    date: "2026-09-27",
    changes: [
      { type: "fixed", text: "Deleting an invoice also deletes its deposit, balance and milestone invoices, so you no longer get overdue reminders for invoices that were deleted." },
    ],
  },
  {
    version: "2026.08.05",
    date: "2026-08-05",
    changes: [
      { type: "fixed", text: "Tax registration numbers on quotes and invoices always match what's in your settings." },
    ],
  },
  {
    version: "2026.07",
    date: "2026-07-23",
    title: "Payment plans reworked",
    changes: [
      { type: "new", text: "Setting a deposit or progress payments on an invoice creates the deposit, balance and milestone invoices as drafts straight away, with the balance owing shown on the parent." },
      { type: "new", text: "Add a line item directly inside a group on quotes and invoices." },
      { type: "new", text: "Edit to-do labels right on the dashboard." },
      { type: "improved", text: "Deposit, balance and milestone invoices show the full scope of work. Ones you've already sent or been paid for no longer change when the parent is edited." },
      { type: "improved", text: "Line items save automatically as you type, so changes aren't lost." },
      { type: "improved", text: "Line item cost badges show profit and margin together, plus the total cost per line." },
      { type: "improved", text: "PDFs show the customer's address and each group's total." },
      { type: "fixed", text: "You're no longer signed out unexpectedly when coming back to a QuoTrak tab." },
    ],
  },
  {
    version: "2026.06",
    date: "2026-06-28",
    title: "Change orders, PDF import and field tools",
    changes: [
      { type: "new", text: "Change orders on projects." },
      { type: "new", text: "Import invoices from PDF files (Pro)." },
      { type: "new", text: "Capture receipts and add them to a project's actual costs." },
      { type: "new", text: "Stair Stringer layout tool in Field Tools." },
      { type: "new", text: "Choose what hour notification emails arrive, in your own time zone." },
      { type: "improved", text: "Info tips explain how totals, margins and other calculations are worked out." },
      { type: "fixed", text: "Draft invoices are no longer marked overdue, and reminders only go out on the daily schedule." },
    ],
  },
  {
    version: "2026.05",
    date: "2026-05-31",
    title: "Projects, calendar and field work",
    changes: [
      { type: "new", text: "Projects (Pro): group quotes, invoices and costs for each job." },
      { type: "new", text: "Calendar view of your active projects." },
      { type: "new", text: "Field section: jobsite photos with GPS location, time logging and a field worker role for your crew." },
      { type: "new", text: "Select, edit and export photos in bulk, and share photo reports." },
      { type: "new", text: "Xero integration: push quotes and invoices to Xero." },
      { type: "new", text: "Set your own labor rate in Settings → Profile." },
      { type: "improved", text: "QuickBooks sync status is shown in your quote and invoice lists." },
      { type: "improved", text: "You're prompted to mark an invoice as sent before exporting its PDF, so the payment link is never missed." },
      { type: "improved", text: "Create a customer on the spot when using a template." },
    ],
  },
  {
    version: "2026.04",
    date: "2026-04-30",
    title: "QuoTrak launches",
    changes: [
      { type: "new", text: "Quotes and invoices with customers, a products and services catalog, a cost catalog and templates." },
      { type: "new", text: "Customer and internal PDFs, with your logo, colors and terms." },
      { type: "new", text: "Accept online payments through Stripe payment links." },
      { type: "new", text: "Deposits and progress payments (milestone billing) on invoices." },
      { type: "new", text: "Recurring invoices." },
      { type: "new", text: "Discounts on quotes and invoices." },
      { type: "new", text: "Line item groups with color tags." },
      { type: "new", text: "Integrations: QuickBooks, Google Sheets live sync and Zapier." },
      { type: "new", text: "File attachments on customer profiles (Pro)." },
      { type: "new", text: "Referrals: invite a contractor and get a free month." },
      { type: "new", text: "AI assistant in Help & Support." },
    ],
  },
];

/** The version the user is running: the newest release. */
export const CURRENT_VERSION = RELEASES[0].version;
